import { useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import {
  isLoggedIn,
  startLogin,
  logout,
  getStoredSpotifyUserId,
  getStoredDisplayName,
  isAuthorizedInstructor,
} from '../spotify/oauth.js'
import { useRealtimeRoot } from '../hooks/useRealtimeRoot.js'
import { updateNode, removeNode, setNode } from '../firebase/realtime.js'
import { findDuplicate } from '../utils/fuzzyMatch.js'
import { SpotifyRoomPoller } from '../spotify/poller.js'
import NowPlaying from './NowPlaying.jsx'
import Queue from './Queue.jsx'
import InstructorSettings from './InstructorSettings.jsx'
import RoomPicker from './RoomPicker.jsx'

const ROOM_THEME = {
  main: { accent: 'bg-peak-accent',  border: 'border-peak-accent/40',  text: 'text-peak-accent',  label: 'Main Room' },
  late: { accent: 'bg-peak-accent2', border: 'border-peak-accent2/40', text: 'text-peak-accent2', label: 'Room 2' },
}

export default function InstructorView() {
  const [loggedIn, setLoggedIn] = useState(isLoggedIn())
  const [showSettings, setShowSettings] = useState(false)
  const { venue, rooms, requests, played, error } = useRealtimeRoot()
  const spotifyUserId = getStoredSpotifyUserId()
  const spotifyDisplayName = getStoredDisplayName()

  // Which room (if any) does this instructor currently have claimed?
  // Claims are manual-only — phone screen-locking won't release them. The
  // claim is "ours" simply when active_instructor matches our Spotify ID.
  const claimedRoomId = useMemo(() => {
    if (!spotifyUserId) return null
    for (const id of ['main', 'late']) {
      if (rooms[id]?.active_instructor === spotifyUserId) return id
    }
    return null
  }, [rooms, spotifyUserId])

  // Run the Spotify poller for whichever room is claimed.
  useEffect(() => {
    if (!loggedIn || !venue.open || !claimedRoomId) return
    const poller = new SpotifyRoomPoller(
      claimedRoomId,
      spotifyUserId,
      venue.opened_at, // backfill cutoff — pre-night listens are filtered out
      undefined,
      () => {
        // someone took over — Firebase will update active_instructor, which
        // will re-render and naturally send us back to the room picker.
      },
    )
    poller.start()
    return () => poller.stop()
  }, [loggedIn, venue.open, venue.opened_at, claimedRoomId, spotifyUserId])

  // ===== Render branches =====

  // Defense-in-depth: if the stored Spotify ID was removed from the allowlist
  if (loggedIn && spotifyUserId && !isAuthorizedInstructor(spotifyUserId)) {
    return <AccessRemoved spotifyUserId={spotifyUserId} onSignOut={() => { logout(); setLoggedIn(false) }} />
  }

  if (!loggedIn) {
    return <SignInScreen />
  }

  return (
    <div className="min-h-full pb-32">
      <Header
        displayName={spotifyDisplayName}
        claimedRoomId={claimedRoomId}
        onShowSettings={() => setShowSettings(true)}
        onSignOut={() => { logout(); setLoggedIn(false) }}
      />

      <main className="max-w-2xl lg:max-w-5xl mx-auto px-4 py-4 space-y-4">
        {error && (
          <div className="card border-red-500/40 text-red-300">
            <p className="text-sm">{error.message}</p>
          </div>
        )}

        <NightControls venue={venue} />

        {!venue.open ? (
          <div className="card text-center space-y-2">
            <p className="text-sm text-peak-muted">
              The night hasn't started yet. Press <strong>Begin the night</strong> above to open
              the dancer view, accept requests, and start tracking what plays.
            </p>
          </div>
        ) : claimedRoomId ? (
          <ClaimedRoomView
            roomId={claimedRoomId}
            roomConfig={rooms[claimedRoomId]}
            requests={requests}
            played={played}
            spotifyUserId={spotifyUserId}
          />
        ) : (
          <RoomPicker
            rooms={rooms}
            myUserId={spotifyUserId}
            onClaimRoom={(roomId) => {
              updateNode(`rooms/${roomId}`, {
                active_instructor: spotifyUserId,
                active_instructor_name: spotifyDisplayName,
                claimed_at: Date.now(),
                last_polled_at: Date.now(),
              })
              updateNode(`instructors/${spotifyUserId}`, {
                current_room: roomId,
                display_name: spotifyDisplayName,
              })
            }}
          />
        )}

        {showSettings && claimedRoomId && (
          <InstructorSettings
            activeRoom={claimedRoomId}
            roomConfig={rooms[claimedRoomId]}
            onClose={() => setShowSettings(false)}
          />
        )}
      </main>
    </div>
  )
}

// =====================================================================

function SignInScreen() {
  return (
    <div className="min-h-full grid place-items-center px-6">
      <div className="card max-w-sm w-full text-center space-y-4">
        <h1 className="text-2xl font-bold">Instructor sign-in</h1>
        <p className="text-sm text-peak-muted">
          Sign in with your <strong>Spotify Premium</strong> account — the same one you DJ from.
          We only read what you're playing.
        </p>
        <button
          className="btn-primary w-full"
          onClick={() => startLogin().catch((e) => alert(e.message))}
        >
          Sign in with Spotify
        </button>
        <Link to="/" className="block text-xs text-peak-muted">
          ← Back to dancer view
        </Link>
      </div>
    </div>
  )
}

function AccessRemoved({ spotifyUserId, onSignOut }) {
  return (
    <div className="min-h-full grid place-items-center px-6">
      <div className="card max-w-sm w-full text-center space-y-4">
        <h1 className="text-xl font-bold">Access removed</h1>
        <p className="text-sm text-peak-muted">
          Your Spotify account ({spotifyUserId}) is no longer on the instructor allowlist.
          Contact the admin to be re-added.
        </p>
        <button className="btn-secondary w-full" onClick={onSignOut}>Sign out</button>
        <Link to="/" className="block text-xs text-peak-muted">← Back to dancer view</Link>
      </div>
    </div>
  )
}

function Header({ displayName, claimedRoomId, onShowSettings, onSignOut }) {
  const theme = claimedRoomId ? ROOM_THEME[claimedRoomId] : null
  return (
    <header className={`sticky top-0 z-10 backdrop-blur border-b border-white/5 ${theme ? 'bg-peak-bg/95' : 'bg-peak-bg/90'}`}>
      <div className="max-w-2xl mx-auto px-4 py-3 flex items-center justify-between">
        <div className="min-w-0">
          <h1 className="text-lg font-bold tracking-tight">
            Peak — Instructor
            {theme && (
              <span className={`pill ${theme.accent} text-white ml-2 align-middle`}>{theme.label}</span>
            )}
          </h1>
          <p className="text-xs text-peak-muted truncate">Signed in as {displayName}</p>
        </div>
        <div className="flex items-center gap-3 flex-none">
          {claimedRoomId && (
            <button onClick={onShowSettings} className="text-sm text-peak-muted hover:text-white">
              Settings
            </button>
          )}
          <button onClick={onSignOut} className="text-xs text-peak-muted hover:text-white">
            Sign out
          </button>
        </div>
      </div>
    </header>
  )
}

function NightControls({ venue }) {
  const [busy, setBusy] = useState(false)

  async function beginNight() {
    setBusy(true)
    try {
      // Wipe yesterday's leftovers so the played list starts at zero from this
      // moment forward — instructors don't want their pre-night listening
      // history visible to dancers. Also reset Room 2's line-dancing state
      // so each night starts in practice mode by default.
      await Promise.all([
        removeNode('requests'),
        removeNode('played_tonight'),
        updateNode('rooms/main', { now_playing: null, queue: null }),
        updateNode('rooms/late', { now_playing: null, queue: null, line_dancing_active: false }),
      ])
      await setNode('venue', { open: true, opened_at: Date.now(), closed_at: null })
    } finally {
      setBusy(false)
    }
  }

  async function endNight() {
    if (!confirm('End the night now? The dancer view will go dark, both rooms will release, and tonight\'s requests + played list will be wiped.')) return
    setBusy(true)
    try {
      // Wipe data + release both rooms + close venue
      await Promise.all([
        removeNode('requests'),
        removeNode('played_tonight'),
        updateNode('rooms/main', { active_instructor: null, claimed_at: null, last_polled_at: null, now_playing: null, queue: null }),
        updateNode('rooms/late', { active_instructor: null, claimed_at: null, last_polled_at: null, now_playing: null, queue: null, line_dancing_active: false }),
      ])
      await setNode('venue', { open: false, opened_at: null, closed_at: Date.now() })
    } finally {
      setBusy(false)
    }
  }

  if (venue.open) {
    return (
      <div className="card flex items-center gap-3 border-peak-ok/30 bg-peak-ok/5">
        <span className="text-2xl" aria-hidden>🟢</span>
        <div className="flex-1">
          <p className="font-semibold">Night is open</p>
          <p className="text-xs text-peak-muted">
            Started {new Date(venue.opened_at || Date.now()).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })}
          </p>
        </div>
        <button onClick={endNight} disabled={busy} className="btn-secondary text-red-300 px-4 py-2">
          End the night
        </button>
      </div>
    )
  }

  return (
    <div className="card flex items-center gap-3 border-peak-accent/30">
      <span className="text-2xl" aria-hidden>🌙</span>
      <div className="flex-1">
        <p className="font-semibold">Night is closed</p>
        <p className="text-xs text-peak-muted">Dancers see the off-hours screen until you open up.</p>
      </div>
      <button onClick={beginNight} disabled={busy} className="btn-primary px-4 py-2">
        {busy ? 'Opening…' : 'Begin the night'}
      </button>
    </div>
  )
}

// =====================================================================

function ClaimedRoomView({ roomId, roomConfig, requests, played, spotifyUserId }) {
  const theme = ROOM_THEME[roomId]
  const isRoom2 = roomId === 'late'
  const lineDancingActive = roomConfig?.line_dancing_active === true

  async function release() {
    if (!confirm(`Release ${theme.label}? It will become available for another instructor to claim.`)) return
    await updateNode(`rooms/${roomId}`, {
      active_instructor: null,
      claimed_at: null,
      last_polled_at: null,
      // Reset Room 2's line-dancing state so the next instructor starts in
      // practice mode by default.
      line_dancing_active: isRoom2 ? false : null,
      // Keep now_playing/queue history intact for dancer view continuity.
    })
  }

  async function toggleLineDancing() {
    const next = !lineDancingActive
    if (next && !confirm('Activate line dancing? The dancer banner will switch from "Practice mode" to "Line Dancing Only".')) return
    if (!next && !confirm('Switch back to practice mode?')) return
    await updateNode(`rooms/${roomId}`, { line_dancing_active: next })
  }

  return (
    <div className="space-y-4">
      <div className={`card border-2 ${theme.border} flex items-center justify-between gap-3`}>
        <div className="min-w-0">
          <p className="text-xs text-peak-muted">You're running</p>
          <p className={`text-lg font-bold ${theme.text} truncate`}>{theme.label}</p>
        </div>
        <button onClick={release} className="btn-secondary text-peak-muted px-3 py-2 text-sm">
          Release room
        </button>
      </div>

      {/* Room 2 — practice / line-dancing toggle */}
      {isRoom2 && (
        <div className={`card border-2 ${theme.border} flex items-center gap-3`}>
          <div className="flex-1 min-w-0">
            <p className={`font-bold ${theme.text}`}>
              {lineDancingActive ? 'Line Dancing Only' : 'Practice mode'}
            </p>
            <p className="text-xs text-peak-muted mt-0.5">
              {lineDancingActive
                ? 'Dancers see the "Line Dancing Only" banner. Switch back if you reopen the floor to swing.'
                : 'Dancers see the "Practice mode" banner. Activate when you\'re ready to start the line dance set.'}
            </p>
          </div>
          <button
            onClick={toggleLineDancing}
            className={`flex-none font-semibold px-4 py-2 rounded-xl active:scale-95 transition-transform ${
              lineDancingActive
                ? 'btn-secondary text-peak-muted'
                : `${theme.accent} text-white shadow-lg shadow-peak-accent2/20`
            }`}
          >
            {lineDancingActive ? 'Back to practice' : 'Activate line dancing'}
          </button>
        </div>
      )}

      <NowPlaying nowPlaying={roomConfig?.now_playing} />

      <section>
        <h2 className="font-semibold mb-2">Coming up next (your Spotify queue)</h2>
        <Queue queue={roomConfig?.queue} />
      </section>

      {/* Requests are always available once the room is claimed — works the
          same way in practice mode and active line-dancing mode. */}
      <InstructorRequestList activeRoom={roomId} requests={requests} played={played} />
    </div>
  )
}

function InstructorRequestList({ activeRoom, requests, played }) {
  const [statusTab, setStatusTab] = useState('pending') // pending | queued | played
  const isMainRoom = activeRoom === 'main'

  // Counts per status for the tab labels.
  const counts = useMemo(() => {
    const c = { pending: 0, queued: 0, played: 0 }
    for (const r of requests) {
      if (r.room !== activeRoom) continue
      if (r.status === 'pending' || r.status === 'queued' || r.status === 'played') c[r.status]++
    }
    return c
  }, [requests, activeRoom])

  // Sort helper — same rules everywhere:
  //   • pending → votes desc, alphabetical tie-break
  //   • queued / played → alphabetical only
  const sortItems = (items) => {
    const byTitleAsc = (a, b) =>
      (a.song_title || '').localeCompare(b.song_title || '', undefined, { sensitivity: 'base' })
    const byVotesThenTitle = (a, b) => b.voteCount - a.voteCount || byTitleAsc(a, b)
    return statusTab === 'pending' ? [...items].sort(byVotesThenTitle) : [...items].sort(byTitleAsc)
  }

  // Three filtered slices for the three render targets:
  //   • visibleAll  → mobile single-column (any layout) and Room 2
  //   • visibleSwing / visibleLine → desktop side-by-side columns (Main only)
  const visibleAll = useMemo(
    () => sortItems(requests.filter((r) => r.room === activeRoom && r.status === statusTab)),
    // sortItems depends on statusTab via closure; deps below are sufficient.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [requests, activeRoom, statusTab],
  )
  const visibleSwing = useMemo(
    () =>
      sortItems(
        requests.filter(
          (r) => r.room === activeRoom && r.status === statusTab && r.dance_type !== 'line',
        ),
      ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [requests, activeRoom, statusTab],
  )
  const visibleLine = useMemo(
    () =>
      sortItems(
        requests.filter(
          (r) => r.room === activeRoom && r.status === statusTab && r.dance_type === 'line',
        ),
      ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [requests, activeRoom, statusTab],
  )

  const emptyCopy =
    statusTab === 'pending'
      ? 'No pending requests right now.'
      : statusTab === 'queued'
        ? 'Nothing queued yet — mark a pending request as queued when you add it to your Spotify queue.'
        : 'No songs have been played yet tonight.'

  return (
    <section>
      <h2 className="font-semibold mb-2">Requests</h2>

      <div className="flex gap-1 bg-peak-bg rounded-lg p-0.5 text-xs font-medium mb-3">
        {[
          { id: 'pending', label: 'To be decided' },
          { id: 'queued',  label: 'Queued' },
          { id: 'played',  label: 'Played' },
        ].map((tab) => (
          <button
            key={tab.id}
            onClick={() => setStatusTab(tab.id)}
            className={`flex-1 py-1.5 rounded transition-colors ${
              statusTab === tab.id
                ? 'bg-white/10 text-white'
                : 'text-peak-muted hover:text-white'
            }`}
          >
            {tab.label} ({counts[tab.id]})
          </button>
        ))}
      </div>

      {/*
        Layout strategy:
          • Main Room on desktop (lg+): two-column grid, line dance on the
            LEFT and swing on the RIGHT. Order matches the dancer view's
            tab order and the request form's dance-type buttons. Lets the
            instructor scan both at a glance instead of toggling tabs.
          • Main Room on mobile: single mixed list (current behavior).
          • Room 2 (any size): single list — Room 2 is line-only, so a
            second column would always be empty.
      */}
      {isMainRoom ? (
        <>
          <div className="hidden lg:grid lg:grid-cols-2 lg:gap-4">
            <DanceColumn
              title="Line dance"
              items={visibleLine}
              played={played}
              emptyCopy={emptyCopy}
            />
            <DanceColumn
              title="Swing"
              items={visibleSwing}
              played={played}
              emptyCopy={emptyCopy}
            />
          </div>
          <ul className="space-y-2 lg:hidden">
            {visibleAll.map((r) => (
              <InstructorRequestRow key={r.id} request={r} played={played} />
            ))}
            {visibleAll.length === 0 && (
              <p className="card text-peak-muted text-sm">{emptyCopy}</p>
            )}
          </ul>
        </>
      ) : (
        <ul className="space-y-2">
          {visibleAll.map((r) => (
            <InstructorRequestRow key={r.id} request={r} played={played} />
          ))}
          {visibleAll.length === 0 && (
            <p className="card text-peak-muted text-sm">{emptyCopy}</p>
          )}
        </ul>
      )}
    </section>
  )
}

/**
 * Single dance-type column for the desktop side-by-side instructor view.
 * Header shows the dance label plus the count for the current status tab.
 */
function DanceColumn({ title, items, played, emptyCopy }) {
  return (
    <div>
      <h3 className="text-sm font-semibold text-peak-muted mb-2">
        {title} <span className="text-peak-muted/60">({items.length})</span>
      </h3>
      <ul className="space-y-2">
        {items.map((r) => (
          <InstructorRequestRow key={r.id} request={r} played={played} />
        ))}
        {items.length === 0 && (
          <p className="card text-peak-muted text-sm">{emptyCopy}</p>
        )}
      </ul>
    </div>
  )
}

function InstructorRequestRow({ request, played }) {
  const dup = useMemo(() => findDuplicate(request, played), [request, played])
  const [busy, setBusy] = useState(false)
  const isQueued = request.status === 'queued'
  const isPlayed = request.status === 'played'
  const isLocked = isQueued || isPlayed
  // Room 2 is line-dance only — the dance-type switcher only makes sense
  // for Main Room requests, where both swing and line are valid.
  // Allowed for pending AND queued rows (instructor can still recategorize
  // after queueing a mis-tagged request); blocked for played rows since
  // the song already happened and reclassifying it doesn't change
  // anything meaningful for the night.
  const canSwitchDanceType = request.room === 'main' && !isPlayed
  const otherDanceType = request.dance_type === 'swing' ? 'line' : 'swing'

  // Mark this request as queued — the song stays in the visible list,
  // dimmed and not votable. The actual play detection happens via Spotify
  // polling, which writes to /played_tonight AND auto-flips matching
  // queued requests to status="played" via fuzzy matching.
  async function markQueued() {
    setBusy(true)
    try {
      await updateNode(`requests/${request.id}`, {
        status: 'queued',
        queued_at: Date.now(),
      })
    } finally {
      setBusy(false)
    }
  }

  // Reverse of markQueued — flip a queued row back to pending so dancers
  // can vote on it again and the instructor can re-queue later. Used when
  // the instructor accidentally tapped "Mark queued" or changed their
  // mind about including the song.
  async function unqueue() {
    if (!confirm('Move this song back to "To be decided"? Dancers can vote on it again.')) return
    setBusy(true)
    try {
      await updateNode(`requests/${request.id}`, {
        status: 'pending',
        queued_at: null,
      })
    } finally {
      setBusy(false)
    }
  }

  // Flip dance_type between "swing" and "line". Used when a dancer
  // mis-tagged their request — the instructor can reassign it without
  // making the dancer delete-and-resubmit. Allowed for pending AND
  // queued rows so the instructor can fix a mis-tag even after the
  // request is in their queue. Confirmation prompt for queued rows
  // since flipping the type after queueing is less common and worth a
  // double-check (avoids accidental taps next to "Delete").
  async function switchDanceType() {
    if (isQueued) {
      const ok = confirm(
        `Reassign this queued request as ${otherDanceType === 'swing' ? 'swing' : 'line dance'}?`,
      )
      if (!ok) return
    }
    setBusy(true)
    try {
      await updateNode(`requests/${request.id}`, {
        dance_type: otherDanceType,
      })
    } finally {
      setBusy(false)
    }
  }

  async function deleteRequest() {
    if (!confirm('Delete this request? It will disappear for everyone.')) return
    setBusy(true)
    try {
      await removeNode(`requests/${request.id}`)
    } finally {
      setBusy(false)
    }
  }

  return (
    <li className={`card space-y-2 ${isLocked ? 'opacity-75' : ''}`}>
      <div className="flex items-center gap-3">
        {request.album_art ? (
          <img
            src={request.album_art}
            alt=""
            className="w-12 h-12 rounded object-cover flex-none"
          />
        ) : (
          <div className="w-12 h-12 rounded bg-white/5 grid place-items-center flex-none text-peak-muted">
            ♪
          </div>
        )}
        <div className="flex-1 min-w-0">
          <p className="font-medium truncate">{request.song_title}</p>
          <p className="text-xs text-peak-muted truncate">
            {request.artist || 'no artist'} · <span className="capitalize">{request.dance_type}</span>
            {isQueued && <span className="ml-1 text-peak-warn">· queued</span>}
            {isPlayed && <span className="ml-1 text-peak-ok">· played</span>}
          </p>
        </div>
        <span className="pill bg-peak-accent/20 text-peak-accent flex-none">♥ {request.voteCount}</span>
      </div>

      {dup && !isLocked && (
        <div className="text-xs px-3 py-2 rounded-lg bg-peak-warn/15 text-peak-warn border border-peak-warn/30">
          ⚠ Already played in <strong>{dup.room === 'main' ? 'Main Room' : 'Room 2'}</strong>{' '}
          ({dup.song_title}{dup.artist ? ` — ${dup.artist}` : ''})
        </div>
      )}

      <div className="flex gap-2">
        {/* Pending: "Mark queued" pushes the request into the queue.
            Queued: "Unqueue" returns it to the pending pool so dancers
            can vote on it again. Both take the wide flex-1 slot. Played
            rows skip both — only Delete remains. */}
        {request.status === 'pending' && (
          <button onClick={markQueued} disabled={busy} className="btn-primary flex-1 py-2">
            Mark queued
          </button>
        )}
        {request.status === 'queued' && (
          <button onClick={unqueue} disabled={busy} className="btn-secondary flex-1 py-2 text-peak-muted">
            ↩ Unqueue
          </button>
        )}
        {canSwitchDanceType && (
          <button
            onClick={switchDanceType}
            disabled={busy}
            className="btn-secondary py-2 px-3 text-xs text-peak-muted"
            title={`Reassign this request as ${otherDanceType === 'swing' ? 'swing' : 'line dance'}`}
          >
            {/*
              Arrow direction telegraphs where the row will move on the
              desktop side-by-side layout (line on the LEFT, swing on the
              RIGHT):
                • current=line  → switch to swing → row moves RIGHT → "→ Swing"
                • current=swing → switch to line  → row moves LEFT  → "← Line"
              On mobile (single mixed list, no left/right meaning) we use a
              double-headed arrow so the button doesn't lie about direction.
            */}
            <span className="hidden lg:inline">
              {otherDanceType === 'swing' ? '→ Swing' : '← Line'}
            </span>
            <span className="lg:hidden">
              ↔ {otherDanceType === 'swing' ? 'Swing' : 'Line'}
            </span>
          </button>
        )}
        <button
          onClick={deleteRequest}
          disabled={busy}
          className={`btn-secondary py-2 ${isPlayed ? 'flex-1' : 'px-3'} text-peak-muted`}
          aria-label="Delete request"
        >
          {isLocked ? 'Delete' : '✕'}
        </button>
      </div>
    </li>
  )
}
